// Per-type facts for the booking sheet's Details block (DC62; design Others.dc.html
// vDetail + factRows, customer gap §A.3 B3). The design switches on seven hard-coded
// booking types; here the booking's OWN template decides (API-212): the scheduling
// model names the time facts (check-in/check-out, session, show, slot, starts/ends),
// and every answered, non-bound template field becomes a labelled fact from
// `type_data`, in the template's field order. Labels come from the template
// (`label_en`/`label_bn`), values through packages/i18n (CB-15).
import type { Lang } from '@bookingdei/i18n/translate';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatDate, formatDayPhrase } from '@bookingdei/i18n/format-date';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import type { Booking, Template, TemplateField } from '../../mock/seeds/schema-types';

export interface Fact {
  label: string;
  value: string;
}

type T = (key: MessageKey) => string;

function when(lang: Lang, iso: string | undefined, now: Date): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : formatDayPhrase(lang, d, now);
}

// The time facts a scheduling model implies (the design's per-type first rows).
function timeFacts(
  b: Booking,
  model: Template['scheduling_model'] | undefined,
  lang: Lang,
  t: T,
  now: Date,
): Fact[] {
  const out: Fact[] = [];
  const push = (key: MessageKey, value: string) => value && out.push({ label: t(key), value });
  switch (model) {
    case 'date_range':
      push('user.bk.fact.checkin', when(lang, b.starts_at, now));
      push('user.bk.fact.checkout', when(lang, b.ends_at, now));
      break;
    case 'block_booking':
      push('user.bk.fact.starts', when(lang, b.starts_at, now));
      push('user.bk.fact.ends', when(lang, b.ends_at, now));
      break;
    case 'serial_session':
    case 'capacity_session':
      push('user.bk.fact.session', when(lang, b.starts_at, now));
      break;
    case 'seat_inventory': {
      push('user.bk.fact.show', when(lang, b.starts_at, now));
      const seats = (b.items ?? []).flatMap((i) => i.seat_refs ?? []);
      if (seats.length) push('user.bk.fact.seats', seats.join(', '));
      break;
    }
    default:
      push('user.bk.fact.slot', when(lang, b.starts_at, now));
  }
  return out;
}

function optionLabel(field: TemplateField, value: unknown, lang: Lang): string {
  const opt = (field.options ?? []).find((o) => o.value === String(value));
  if (!opt) return String(value);
  return (lang === 'bn' ? opt.label_bn : opt.label_en) ?? opt.label_en ?? String(value);
}

/** One answer rendered for display, or '' when there is nothing to show. */
export function answerText(field: TemplateField, value: unknown, lang: Lang, t: T): string {
  if (value === undefined || value === null || value === '') return '';
  switch (field.kind) {
    case 'toggle':
      return t(value ? 'user.bk.fact.yes' : 'user.bk.fact.no');
    case 'number':
    case 'counter':
      return typeof value === 'number' ? formatNumber(lang, value) : String(value);
    case 'select':
      return optionLabel(field, value, lang);
    case 'multi_select':
      return Array.isArray(value)
        ? value.map((v) => optionLabel(field, v, lang)).join(', ')
        : optionLabel(field, value, lang);
    case 'date': {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : formatDate(lang, d);
    }
    case 'file':
      // Private uploads are never rendered inline (API-22).
      return '';
    default:
      return Array.isArray(value) ? value.join(', ') : String(value);
  }
}

export function templateFacts(
  b: Booking,
  template: Template | undefined,
  lang: Lang,
  t: T,
): Fact[] {
  const data = (b.type_data ?? {}) as Record<string, unknown>;
  const out: Fact[] = [];
  for (const field of template?.fields ?? []) {
    if (field.bound_to) continue; // scheduling-owned: shown as a time fact / the serial tile
    const text = answerText(field, data[field.key], lang, t);
    if (text)
      out.push({
        label: (lang === 'bn' ? field.label_bn : field.label_en) || field.key,
        value: text,
      });
  }
  return out;
}

/** The full Details grid: time facts, template answers, services, notes, the reschedule. */
export function bookingFacts(
  b: Booking,
  template: Template | undefined,
  lang: Lang,
  t: T,
  now: Date,
): Fact[] {
  const facts = [
    ...timeFacts(b, template?.scheduling_model, lang, t, now),
    ...templateFacts(b, template, lang, t),
  ];
  const items = b.items ?? [];
  if (items.length > 1)
    facts.push({
      label: t('user.bk.fact.services'),
      value: items.map((i) => i.name_snapshot).join(', '),
    });
  if (b.customer_note) facts.push({ label: t('user.bk.fact.notes'), value: b.customer_note });
  const moved = [...(b.events ?? [])].reverse().find((e) => e.action === 'rescheduled');
  const detail = (moved?.detail ?? {}) as { from_starts_at?: string; to_starts_at?: string };
  if (detail.from_starts_at && detail.to_starts_at) {
    const from = when(lang, detail.from_starts_at, now);
    const to = when(lang, detail.to_starts_at, now);
    facts.push({
      label: t('user.bk.fact.rescheduled'),
      value: formatMessage(lang, 'user.bk.fact.moved', { from, to }),
    });
  }
  return facts;
}
